import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    alpha,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Link,
    Stack,
    type SxProps,
    type Theme,
    Tooltip,
    Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import Chip from '/@/components/bayer/chip';
import Icon from '/@/components/icon';
import DekalbLogo from '/@/components/new-login/assets/logo_dekalb.svg';
import colors from '/@/components/theme-provider/colors';
import settings from '/@/core/settings';
import { useAllCatalogProducts } from '/@/hooks/catalog';
import { _ } from '/@/locale';
import { getCatalogBrand } from '/@/store/catalog/selectors';
import { getSessionCountry } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { reverse } from '/@/utils/router';
import CPWhenToApplyLogo from '../../assets/cp-when-to-apply-logo.svg';
import useProductsFilterOptions from '../../hooks/product-filter-options';
import type { CornCycle } from '../product-card';

export const useFilters = () => {
    const [filters, setFilters] = useState<Record<string, Set<string | number>>>({});

    const toggleFilterItem = ({ filter, value }: { filter: string; value: string | number }) => {
        setFilters((prevFilters) => {
            const updatedFilters = { ...prevFilters };

            if (!updatedFilters[filter]) {
                updatedFilters[filter] = new Set();
            } else {
                updatedFilters[filter] = new Set(updatedFilters[filter]); // Create a new Set
            }

            if (updatedFilters[filter].has(value)) {
                updatedFilters[filter].delete(value);
            } else {
                updatedFilters[filter].add(value);
            }
            return updatedFilters;
        });
    };

    return { filters, setFilters, toggleFilterItem };
};

export const ProductSelectorButton = ({
    isSelected,
    label,
    onClick,
    icon,
    sx,
    color = 'primary',
    p = { py: 1.5, px: 1 },
    disabled = false,
}: {
    isSelected: boolean;
    label: string;
    onClick: VoidFunction;
    icon?: string;
    sx?: SxProps<Theme>;
    color?: 'primary' | 'secondary';
    p?: {
        py?: number;
        px?: number;
    };
    disabled?: boolean;
}) => {
    const style = [
        {
            borderRadius: 0.5,
            border: (t) => `1px solid ${t.palette.divider}`,
            background: '#F5F5F5',
            color: 'primary.main',
            cursor: 'pointer',
        },
        isSelected && {
            border: (t) => `1px solid ${t.palette.secondary.main}`,
            background: (t) => alpha(t.palette.secondary.main, 0.1),
        },
        disabled && {
            background: (t) => t.palette.divider,
            opacity: 0.5,
            cursor: 'unset',
        },
    ];

    return (
        <Tooltip title={disabled ? _('Opción no disponible') : ''} arrow>
            <Stack
                sx={style}
                onClick={disabled ? undefined : onClick}
                py={p?.py}
                px={p?.px}
                direction='row'
                spacing={1}
                alignItems='center'
            >
                {icon && (
                    <Box width='18px' height='18px'>
                        <Icon type={icon} size={'medium'} />
                    </Box>
                )}
                <Typography color={color} variant='subtitle2' fontWeight='bold' sx={sx}>
                    {label}
                </Typography>
            </Stack>
        </Tooltip>
    );
};

const ProductsSelector = ({
    type,
    resetFilters,
}: {
    type: 'corn' | 'soy' | 'crop_protection';
    resetFilters: VoidFunction;
}) => {
    const navigate = useNavigate();
    const country = useSelector(getSessionCountry);
    const cropProtectionBrand = useSelector((state) => getCatalogBrand(state, 'crop_protection'));
    const cropProtectionProductsQuery = useAllCatalogProducts({
        brand: cropProtectionBrand?.name,
        crop: 'crop_protection',
        country: country!.code!,
        cropProtectionCrop: undefined,
        cropProtectionProductType: undefined,
    });
    const cropProtectionProductsEnabled = cropProtectionProductsQuery.data?.results?.length > 0;

    return (
        <Accordion defaultExpanded disableGutters elevation={0} sx={{ pt: 1 }}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ '.MuiAccordionSummary-content': { my: 0 }, minHeight: 1, p: 0 }}
            >
                <Typography color='primary' fontWeight='bold'>
                    {_('Producto')}
                </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, py: 1 }}>
                <Stack direction='column' spacing={1}>
                    <ProductSelectorButton
                        isSelected={['corn', 'soy'].includes(type)}
                        onClick={() => {
                            resetFilters();
                            navigate(
                                reverse('productExploration.list', {
                                    params: {
                                        type: 'corn',
                                        slug: 'dekalb',
                                    },
                                })
                            );
                        }}
                        label={_('Híbridos Dekalb')}
                        icon='cornNew'
                    />
                    <ProductSelectorButton
                        disabled={!cropProtectionProductsEnabled}
                        isSelected={type === 'crop_protection'}
                        onClick={() => {
                            resetFilters();
                            navigate(
                                reverse('productExploration.list', {
                                    params: {
                                        type: 'crop_protection',
                                        slug: 'bayer',
                                    },
                                })
                            );
                        }}
                        label={_('Protección de cultivos')}
                        icon='cropProtection'
                    />
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
};

const FilterAccordion = ({
    title,
    options,
    filterKey,
    filters,
    toggleFilterItem,
    parseValue = (value) => value,
}: {
    title: string;
    options: [string, string][];
    filterKey: string;
    filters: Record<string, Set<string | number>>;
    toggleFilterItem: ({ filter, value }: { filter: string; value: string | number }) => void;
    parseValue?: (value: string) => any;
}) => {
    if (options.length === 0) return null;

    return (
        <Accordion defaultExpanded disableGutters elevation={0} sx={{ pt: 1 }}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ '.MuiAccordionSummary-content': { my: 0 }, minHeight: 1, p: 0 }}
            >
                <Typography color='primary' fontWeight='bold'>
                    {title}
                </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, py: 1 }}>
                <FormGroup>
                    {options.map(([id, name]) => (
                        <FormControlLabel
                            key={id}
                            control={
                                <Checkbox
                                    onChange={() => toggleFilterItem({ filter: filterKey, value: parseValue(id) })}
                                    checked={filters[filterKey]?.has(parseValue(id)) || false}
                                />
                            }
                            label={name}
                        />
                    ))}
                </FormGroup>
            </AccordionDetails>
        </Accordion>
    );
};

const ProductExplorationListSidebar = ({
    products,
    type,
    totalProducts,

    filters,
    setFilters,
    toggleFilterItem,
    setSearch,
}: {
    products: any[];
    type: 'corn' | 'soy' | 'crop_protection';
    totalProducts: number;

    filters: ReturnType<typeof useFilters>['filters'];
    setFilters: ReturnType<typeof useFilters>['setFilters'];
    toggleFilterItem: ReturnType<typeof useFilters>['toggleFilterItem'];
    setSearch: (s: string) => void;
}) => {
    const navigate = useNavigate();

    const {
        technologyOptions,
        technologyOptionsArray,
        zoneOptions,
        zoneOptionsArray,
        typeOptions,
        typeOptionsArray,
        cropOptions,
        cropOptionsArray,
        cycleOptions,
        cycleOptionsArray,
        positioningOptions,
        positioningOptionsArray,
    } = useProductsFilterOptions(products);

    const Chips = useMemo(
        () =>
            Object.entries(filters).flatMap(([filter, values = []]) => {
                return Array.from(values).map((value) => {
                    let label: string | undefined = '';
                    if (filter === 'technologies') label = technologyOptions?.[Number(value)];
                    if (filter === 'zones') label = zoneOptions?.[Number(value)];
                    if (filter === 'types') label = typeOptions?.[Number(value)];
                    if (filter === 'crops') label = cropOptions?.[Number(value)];
                    if (filter === 'cycles') label = cycleOptions?.[value as CornCycle];
                    if (filter === 'positioning') label = positioningOptions?.[value as string];

                    return (
                        <Chip
                            key={`${filter}${value}`}
                            onDelete={() => toggleFilterItem({ filter, value })}
                            label={label}
                            sx={{
                                background: colors.themes[settings.app.theme].mui.primary.infoBackground,
                            }}
                        />
                    );
                });
            }),
        [filters, technologyOptions, zoneOptions, typeOptions, cropOptions, cycleOptions, positioningOptions]
    );

    const resetFilters = () => {
        setFilters({});
        setSearch('');
    };

    return (
        <Stack spacing={4}>
            <Stack spacing={2}>
                {Chips.length > 0 && (
                    <Stack spacing={0.5}>
                        <Stack direction='row' justifyContent='space-between' alignItems='center'>
                            <Typography variant='caption' color='primary.mui.inactive'>
                                {_('{{number}} resultados.', {
                                    number: totalProducts,
                                })}
                            </Typography>
                            <Link
                                sx={[
                                    {
                                        ml: 'auto',
                                        width: 'auto',
                                    },
                                ]}
                            >
                                <Typography
                                    onClick={resetFilters}
                                    sx={[
                                        {
                                            cursor: 'pointer',
                                        },
                                    ]}
                                    color='primary'
                                    variant='caption'
                                >
                                    {_('Borrar todo')}
                                </Typography>
                            </Link>
                        </Stack>
                        <Stack direction='row' flexWrap='wrap' spacing={0.5} useFlexGap>
                            {Chips}
                        </Stack>
                    </Stack>
                )}
                <ProductsSelector type={type} resetFilters={resetFilters} />

                {type === 'crop_protection' && (
                    <>
                        <FilterAccordion
                            title={_('Tipo')}
                            options={typeOptionsArray}
                            filterKey='types'
                            filters={filters}
                            toggleFilterItem={toggleFilterItem}
                            parseValue={(value: string) => Number(value)}
                        />
                        <FilterAccordion
                            title={_('Cultivo')}
                            options={cropOptionsArray}
                            filterKey='crops'
                            filters={filters}
                            toggleFilterItem={toggleFilterItem}
                            parseValue={(value: string) => Number(value)}
                        />
                    </>
                )}

                {['corn', 'soy'].includes(type) && (
                    <>
                        <FilterAccordion
                            title={_('Zona')}
                            options={zoneOptionsArray}
                            filterKey='zones'
                            filters={filters}
                            toggleFilterItem={toggleFilterItem}
                            parseValue={(value: string) => Number(value)}
                        />
                        <FilterAccordion
                            title={_('Ciclo')}
                            options={cycleOptionsArray}
                            filterKey='cycles'
                            filters={filters}
                            toggleFilterItem={toggleFilterItem}
                        />
                        <FilterAccordion
                            title={_('Posicionamiento ideal')}
                            options={positioningOptionsArray}
                            filterKey='positioning'
                            filters={filters}
                            toggleFilterItem={toggleFilterItem}
                        />
                        <FilterAccordion
                            title={_('Tecnologias')}
                            options={technologyOptionsArray}
                            filterKey='technologies'
                            filters={filters}
                            toggleFilterItem={toggleFilterItem}
                            parseValue={(value: string) => Number(value)}
                        />
                    </>
                )}
            </Stack>

            {/*  <FormGroup
                sx={{
                    backgroundColor: '#F5F5F5', // Use theme-based color
                    px: 2,
                    py: 1,
                }}
            >
                <FormControlLabel
                    control={<Switch onChange={() => {}} checked={false} disabled />}
                    label={
                        <Typography color='primary' variant='body2' fontWeight='500'>
                            {_('Solo los que tienen stock')}
                        </Typography>
                    }
                    labelPlacement='start'
                />
            </FormGroup> */}

            <Stack
                p={3}
                justifyContent='center'
                alignItems='center'
                textAlign='center'
                sx={{
                    backgroundColor: '#F5F5F5', // Use theme-based color
                }}
                spacing={3}
            >
                {['corn', 'soy'].includes(type) ? (
                    <>
                        <Box component='img' src={DekalbLogo} alt='dekalb' height='55px' />
                        <Stack spacing={1}>
                            <Typography color='primary' variant='h5'>
                                {_('La tecnología más competitiva del mercado')}
                            </Typography>
                            <Typography color='primary' variant='body1'>
                                {_(
                                    'Presentamos las mejores tecnologías que protegen tus cultivos, minimizan tus riesgos y maximizan tu rinde.'
                                )}
                            </Typography>
                        </Stack>
                        <Button
                            variant='contained'
                            fullWidth
                            onClick={() => navigate(reverse('productExploration.technology'))}
                        >
                            {_('Ver Tecnologías')}
                        </Button>
                    </>
                ) : (
                    <>
                        <Box component='img' src={CPWhenToApplyLogo} alt='when-to-apply' height='65px' />
                        <Stack spacing={1}>
                            <Typography color='primary' variant='h5'>
                                {_('Cuándo aplicar un fitosanitario')}
                            </Typography>
                            <Typography color='primary' variant='body1'>
                                {_('Revisá el momento ideal de aplicación según el cultivo.')}
                            </Typography>
                        </Stack>
                        <Button
                            variant='contained'
                            fullWidth
                            onClick={() => {
                                navigate(reverse('productExploration.cropProtection.whenToApply'));
                                mpTrackEvent('Crop Protection Application Moment Viewed', {});
                            }}
                        >
                            {_('Ver momentos de aplicación')}
                        </Button>
                    </>
                )}
            </Stack>
        </Stack>
    );
};

export default ProductExplorationListSidebar;
